/*!
 * stageGrid.js — the stage cut into columns and rows, and what is in each cell
 * © 2026 Rob Canning — GPLv3
 *
 *   grid(3, 2)                      3 columns, 2 rows over the stage
 *   grid(4)                         4 by 4
 *   grid(cols:[2, 1, 1], rows:2)    the first column twice as wide as the others
 *   viz(s1, cell:4)                 s1's box in cell 4 -- counted along the rows, from 1
 *   viz(s1, cell:[2, 1])            column 2, row 1
 *   viz(s1, cell:4, across:2)       cells 4 and 5; down:2 the ones under them too
 *   viz(s1, cell:free)              an empty cell, at random; cell:any any cell
 *   viz(s1, cell:Pseq(1, 2, 3, inf))  the next cell each time the line runs
 *   grid(shuffle) / grid(turn)      everything in a cell to another at random / one on
 *
 * The cells are shares of the band the screen leaves (viewBand.js), as at:
 * and size: are; viz.js turns a cell into the at: and size: of what it puts
 * there. Here is only the arithmetic, and what is in which cell -- kept, so
 * that a new grid, a shuffle or a turn can put each thing in its cell again.
 */

import { atPair } from "./viewBand.js";

let grid = null;                 // { xs: [0 .. 1], ys: [0 .. 1], gap }
const placed = new Map();        // uid -> { cell (from 0), across, down, size, at }
const streams = new Map();       // uid -> { key, gen }: a cell:Pseq(...) stepping

/** [2, 1, 1] -> [0, 0.5, 0.75, 1]: where each part begins, and the end. */
function edges(spec) {
  const parts = (Array.isArray(spec) ? spec : Array.from({ length: Math.max(1, Math.round(Number(spec) || 1)) }, () => 1))
    .map(Number).map(w => (Number.isFinite(w) && w > 0 ? w : 1));
  const total = parts.reduce((a, b) => a + b, 0);
  const out = [0];
  for (const w of parts) out.push(out[out.length - 1] + w / total);
  out[out.length - 1] = 1;
  return out;
}

/** A grid of `cols` and `rows`: each a count, or a list of widths / heights. */
export function setGrid({ cols = 1, rows = 1, gap = 0 } = {}) {
  grid = { xs: edges(cols), ys: edges(rows), gap: Math.min(0.2, Math.max(0, Number(gap) || 0)) };
  // what was in a cell past the new grid's last: round to the start
  const n = cellCount();
  for (const e of placed.values()) e.cell %= n;
}

/** No grid, and nothing remembered in its cells. */
export function clearGrid() {
  grid = null;
  placed.clear();
  streams.clear();
}

export const hasGrid = () => grid != null;
export const gridSize = () => (grid ? { cols: grid.xs.length - 1, rows: grid.ys.length - 1 } : null);
export const cellCount = () => (grid ? (grid.xs.length - 1) * (grid.ys.length - 1) : 0);

/** The cells taken by something other than `uid`. */
function taken(uid) {
  const out = new Set();
  for (const [u, e] of placed) {
    if (u === uid) continue;
    const { cols } = gridSize();
    const c0 = e.cell % cols, r0 = Math.floor(e.cell / cols);
    for (let r = r0; r < r0 + e.down; r++) for (let c = c0; c < c0 + e.across; c++) out.add(r * cols + c);
  }
  return out;
}

/**
 * The cell a cell: value names, from 0, or null (with why). A number counts
 * along the rows from 1; [col, row] from 1 each; free an empty one at
 * random (any one when all are taken), any any one; a pattern its next
 * value, from the generator `makeGen` makes of it (audioShared.js
 * makeValueGenerator: the caller's, so that this needs no sound).
 */
export function cellOf(value, uid, { random = Math.random, makeGen = null } = {}) {
  if (!grid) return { cell: null, why: "no grid: grid(3, 2) first" };
  const { cols, rows } = gridSize(), n = cols * rows;
  let v = value;
  if (v && typeof v === "object" && v.type === "pattern") {
    const key = JSON.stringify(v);
    let s = streams.get(uid);
    if (s?.key !== key) streams.set(uid, s = { key, gen: makeGen?.(v) });
    v = s.gen?.next();
    if (v == null) return { cell: null, why: "the pattern has ended" };
  }
  if (Array.isArray(v)) {
    const c = Math.round(Number(v[0])), r = Math.round(Number(v[1] ?? 1));
    if (!(c >= 1 && c <= cols && r >= 1 && r <= rows)) return { cell: null, why: `cell:[${v}] -- columns 1..${cols}, rows 1..${rows}` };
    return { cell: (r - 1) * cols + (c - 1) };
  }
  const word = String(v).trim();
  if (word === "any") return { cell: Math.floor(random() * n) };
  if (word === "free") {
    const busy = taken(uid);
    const open = [...Array(n).keys()].filter(i => !busy.has(i));
    const from = open.length ? open : [...Array(n).keys()];
    return { cell: from[Math.floor(random() * from.length)] };
  }
  const k = Math.round(Number(word));
  if (!(k >= 1 && k <= n)) return { cell: null, why: `cell:${word} -- 1..${n}, [col, row], free or any` };
  return { cell: k - 1 };
}

/**
 * Cell `cell` (from 0), `across` columns and `down` rows of it, as shares of
 * the stage: { x, y, w, h }, the gap taken off. A span past the grid's edge
 * is moved back in, so it is always whole.
 */
export function cellArea(cell, across = 1, down = 1) {
  if (!grid) return null;
  const { cols, rows } = gridSize();
  across = Math.min(cols, Math.max(1, Math.round(across) || 1));
  down = Math.min(rows, Math.max(1, Math.round(down) || 1));
  const c = Math.min(cell % cols, cols - across), r = Math.min(Math.floor(cell / cols), rows - down);
  const g = grid.gap / 2;
  const x0 = grid.xs[c] + g, x1 = grid.xs[c + across] - g;
  const y0 = grid.ys[r] + g, y1 = grid.ys[r + down] - g;
  return { x: x0, y: y0, w: Math.max(0, x1 - x0), h: Math.max(0, y1 - y0) };
}

/** A shape in a cell, by default: this much of the cell's shorter side. */
export const CELL_SHAPE = 0.8;

const share = (v, d) => (Number.isFinite(Number(v)) && v !== "" && v != null ? Number(v) : d);

/**
 * What goes in a cell, as the at: and size: it takes (shares of the band,
 * `band` its width and height in px). A box fills its cell; a shape or a
 * drawing (size: one number, of the band's shorter side) is CELL_SHAPE of
 * the cell's shorter side; a text sits at its middle. The size: and at:
 * given with cell: (`entry`) are shares of the cell.
 */
export function cellPlacement(kind, entry, band) {
  const area = cellArea(entry.cell, entry.across, entry.down);
  if (!area) return null;
  const a = atPair(entry.at);
  const fx = Array.isArray(a) ? share(a[0], 0.5) : 0.5, fy = Array.isArray(a) ? share(a[1], 0.5) : 0.5;
  const out = { at: [area.x + fx * area.w, area.y + fy * area.h] };
  if (kind === "text") return out;
  const size = entry.size;
  if (Array.isArray(size) || (kind !== "shape" && kind !== "block")) {
    const [w, h] = size == null ? [1, 1] : Array.isArray(size) ? [share(size[0], 1), share(size[1] ?? size[0], 1)] : [share(size, 1), share(size, 1)];
    out.size = [area.w * w, area.h * h];
  } else {
    const s = share(size, CELL_SHAPE);
    out.size = s * Math.min(area.w * band.width, area.h * band.height) / Math.min(band.width, band.height);
  }
  return out;
}

/** Every cell, numbered from 1, for drawing the grid. */
export const cells = () => Array.from({ length: cellCount() }, (_, i) => ({ n: i + 1, ...cellArea(i) }));

/**
 * `uid` is in this cell now (with what was asked inside it: size:, at:);
 * `ask`, the cell: as the line wrote it. The kept entry is returned: a
 * shuffle or a turn changes its cell, so what places the thing later (a
 * viz() waiting for its box) reads where it is by then.
 *
 * The same line run again (the same cell:4 or cell:[2, 1]) leaves it where
 * a shuffle or a turn has put it -- run back to cell 4, it would land on
 * what the shuffle put there, its own cell left empty. Sent somewhere else
 * (another cell:, a pattern's next), it goes there, and what is there stays:
 * a walker steps onto the stones.
 */
export function remember(uid, entry, ask) {
  const was = placed.get(uid);
  const key = ask === undefined ? undefined : JSON.stringify(ask);
  const fixed = ask != null && (Array.isArray(ask) || Number.isFinite(Number(ask)));
  let cell = entry.cell;
  if (was && fixed && was.ask === key) cell = was.cell;
  const kept = { across: 1, down: 1, ...entry, cell, ask: key };
  placed.set(uid, kept);
  return kept;
}

export function forget(uid) { placed.delete(uid); streams.delete(uid); }
export const placedIn = () => [...placed.entries()].map(([uid, e]) => ({ uid, ...e }));

/**
 * Everything in a cell to another, at random: each its own cell while there
 * are enough (repeats only when more things than cells), none where it was
 * when that can be had. A permutation each try, tried again until nothing
 * lands home -- swapping one home-comer with its neighbour, as before, gave
 * the last one the first one's cell on a full grid: two in a cell, one empty.
 */
export function shuffleCells(random = Math.random) {
  const n = cellCount();
  if (!n) return;
  const items = [...placed.values()];
  const permutation = () => {
    const o = [...Array(n).keys()];
    for (let i = n - 1; i > 0; i--) { const j = Math.floor(random() * (i + 1)); [o[i], o[j]] = [o[j], o[i]]; }
    return o;
  };
  let best = null;
  for (let tries = 0; tries < 100; tries++) {
    const o = permutation();
    const to = items.map((_, i) => o[i % n]);
    const home = to.filter((c, i) => c === items[i].cell).length;
    if (!best || home < best.home) best = { to, home };
    if (!home) break;
  }
  items.forEach((e, i) => { e.cell = best.to[i]; });
}

/** Everything one cell on (step -1: back), round from the last to the first. */
export function turnCells(step = 1) {
  const n = cellCount();
  if (!n) return;
  for (const e of placed.values()) e.cell = (((e.cell + Math.round(step)) % n) + n) % n;
}
